import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AnalyticsEvent, track } from '@bonusly/analytics';
import { useHttpClient } from '@bonusly/http-client';
import { useCurrentUser } from '@bonusly/who-am-i';

import { BizyCompleteEvent, useBizyChannel } from '../../../hooks/use-bizy-channel';
import { executePageAction, useActionSchemaRegistry } from '../../../lib/bizy-actions';
import { useInvalidateBonusesInfinite } from '../../../modules/bonus-feed/features/recognition/cache/invalidators/bonus';
import { QueryKey } from '../../../modules/growth/types/growth-query-key';
import { useBizyCancelMutation, useBizyChatV2Mutation } from '../../../queries/bizy';
import { bizyConversationPath, useBizyUserConversation } from '../../../queries/bizy-conversations';
import { invalidateCelebrationsAdminCaches } from '../../../queries/celebrations';
import { BizyUserConversationDetail } from '../../../schemas/bizy-conversations';
import type { BizyState } from '../../bizy';
import type { NavigateFn } from '../bizy-navigation-context';
import { clearBizySession, loadBizySession, saveBizySession } from '../lib/session-storage';
import { useBizyWidgetStore } from '../store/use-bizy-widget-store';
import { useBizyReportContext } from './use-bizy-report-context';

export type FeedbackType = 'positive' | 'negative' | undefined;

export interface ChatMessage {
	role: 'user' | 'assistant';
	content: string;
	id?: string;
	feedbackType?: FeedbackType;
	key?: string;
	toolsUsed?: string[];
	timestamp?: Date;
}

interface UseBizyGlobalChatOptions {
	extraContext?: Record<string, string>;
	/** Override React Router navigate for non-SPA contexts (e.g. MFE on Rails pages). */
	navigateFn?: NavigateFn;
}

const ROLE_USER = 'user' as const;
const ROLE_ASSISTANT = 'assistant' as const;

const EMOTION_CURIOUS: BizyState = 'curious';
const EMOTION_DISAPPOINTED: BizyState = 'disappointed';

const STREAM_TIMEOUT_MS = 300_000;

let messageKeyCounter = 0;
function nextMessageKey(): string {
	return `msg-${Date.now()}-${messageKeyCounter++}`;
}

let pendingIdCounter = 0;
function nextPendingRequestId(): string {
	return `pending-${Date.now()}-${pendingIdCounter++}`;
}

/**
 * Hook for managing Bizy streaming chat state in admin pages.
 *
 * Features:
 * - request_id filtering with cross-tab sync
 * - 5m streaming timeout with retry
 * - Connection health awareness
 * - Cross-tab message sync via onOtherTabComplete
 * - Refetch conversation on tab focus
 * - Smart 409 handling (disabled input instead of error)
 */
export function useBizyGlobalChat(options: UseBizyGlobalChatOptions = {}) {
	const { extraContext, navigateFn } = options;
	const sessionData = loadBizySession();

	const [messages, setMessages] = useState<ChatMessage[]>(sessionData?.messages || []);
	const [conversationId, setConversationId] = useState<string | undefined>(sessionData?.conversationId);
	const [bizyEmotion, setBizyEmotion] = useState<BizyState>(sessionData?.bizyEmotion || EMOTION_CURIOUS);
	const messagesRef = useRef(messages);
	const bizyEmotionRef = useRef(bizyEmotion);
	messagesRef.current = messages;
	bizyEmotionRef.current = bizyEmotion;
	const [loadConversationId, setLoadConversationId] = useState<string | undefined>();
	const [isLoading, setIsLoading] = useState(false);
	const [isTimedOut, setIsTimedOut] = useState(false);
	const [isOtherTabLoading, setIsOtherTabLoading] = useState(false);
	const [activeToolCalls, setActiveToolCalls] = useState<string[]>([]);

	const { data: loadedConversation, isError: isLoadError } = useBizyUserConversation(loadConversationId);

	const currentUser = useCurrentUser();
	const http = useHttpClient();
	const queryClient = useQueryClient();
	const { mutate } = useBizyChatV2Mutation();
	const { mutate: cancelMutate } = useBizyCancelMutation();
	const { t } = useTranslation('admin_bizy');
	const reportContext = useBizyReportContext((state) => state.context);
	const resumeConversationId = useBizyWidgetStore((state) => state.resumeConversationId);
	const resumeMessages = useBizyWidgetStore((state) => state.resumeMessages);
	const clearResume = useBizyWidgetStore((state) => state.clearResume);
	const routerNavigate = useNavigate();
	const navigate = navigateFn ?? routerNavigate;
	const invalidateBonusesInfinite = useInvalidateBonusesInfinite();
	const { getSchema } = useActionSchemaRegistry();

	const isLoadingRef = useRef(false);
	const activeRequestIdRef = useRef<string | undefined>();
	const streamingContentRef = useRef('');
	const messageSentAtRef = useRef<number | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const lastUserMessageRef = useRef<string>('');
	const requestGenerationRef = useRef(0);
	const conversationIdRef = useRef<string | undefined>();
	conversationIdRef.current = conversationId;

	const skipNextPersistRef = useRef(false);

	// --- Timeout management ---
	const clearStreamTimeout = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = undefined;
		}
	}, []);

	const handleTimeout = useCallback((reason: 'stream_timeout' | 'connection_lost' = 'stream_timeout') => {
		if (!isLoadingRef.current) return;

		track(AnalyticsEvent.Bizy.ResponseReceived, {
			success: false,
			errorType: reason,
			path: window.location.pathname,
		});

		setIsTimedOut(true);
		setIsLoading(false);
		isLoadingRef.current = false;
		setActiveToolCalls([]);
		messageSentAtRef.current = null;
	}, []);

	const resetStreamTimeout = useCallback(() => {
		clearStreamTimeout();
		timeoutRef.current = setTimeout(handleTimeout, STREAM_TIMEOUT_MS);
	}, [clearStreamTimeout, handleTimeout]);

	useEffect(() => clearStreamTimeout, [clearStreamTimeout]);

	// --- Refetch conversation from backend (for cross-tab sync & tab focus) ---
	const refetchConversation = useCallback(
		async (convId: string) => {
			const generation = requestGenerationRef.current;
			try {
				const res = await http.get(bizyConversationPath(convId), {
					headers: { 'Key-Inflection': 'camel' },
				});

				if (requestGenerationRef.current !== generation) return;

				// Only apply if this is still the active conversation (avoids overwriting after dropdown switch).
				if (conversationIdRef.current !== convId) return;

				const parsed = BizyUserConversationDetail.safeParse(res.data.conversation);
				if (!parsed.success) return;

				const hydrated: ChatMessage[] = parsed.data.messages.flatMap((msg) => [
					{ role: ROLE_USER, content: msg.userMessage, key: nextMessageKey(), timestamp: msg.createdAt },
					{
						role: ROLE_ASSISTANT,
						content: msg.bizyResponse,
						id: msg.id,
						feedbackType: msg.feedbackType ?? undefined,
						key: nextMessageKey(),
					},
				]);
				setMessages(hydrated);
			} catch {
				// Silently fail — the in-memory state is still usable
			}
		},
		[http]
	);

	// --- Tab focus: refetch conversation to catch up on other tabs' messages ---
	// Skip when loading a different conversation from dropdown so we don't overwrite with the previous one.
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (loadConversationId) return;
			if (document.visibilityState === 'visible' && conversationIdRef.current && !isLoadingRef.current) {
				refetchConversation(conversationIdRef.current);
				setIsOtherTabLoading(false);
			}
		};
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [refetchConversation, loadConversationId]);

	// Rehydrate conversation from history page or dropdown
	useEffect(() => {
		if (resumeConversationId && resumeMessages) {
			clearStreamTimeout();
			activeRequestIdRef.current = undefined;
			requestGenerationRef.current += 1;

			const hydrated: ChatMessage[] = resumeMessages.flatMap((msg) => [
				{ role: ROLE_USER, content: msg.userMessage, key: nextMessageKey(), timestamp: msg.createdAt },
				{
					role: ROLE_ASSISTANT,
					content: msg.bizyResponse,
					id: msg.id,
					feedbackType: msg.feedbackType ?? undefined,
					key: nextMessageKey(),
				},
			]);
			setMessages(hydrated);
			setConversationId(resumeConversationId);
			setBizyEmotion(EMOTION_CURIOUS);
			setIsLoading(false);
			isLoadingRef.current = false;
			setIsTimedOut(false);
			setIsOtherTabLoading(false);
			setActiveToolCalls([]);
			streamingContentRef.current = '';
			clearResume();

			// Persist immediately; skip the generic persist effect this cycle so it doesn't overwrite with stale state.
			skipNextPersistRef.current = true;
			saveBizySession({ messages: hydrated, conversationId: resumeConversationId, bizyEmotion: EMOTION_CURIOUS });
		}
	}, [resumeConversationId, resumeMessages, clearResume, clearStreamTimeout]);

	// Dropdown: when user selects a conversation, we fetch and apply it here. One effect, no store.
	useEffect(() => {
		if (!loadConversationId || !loadedConversation || loadedConversation.conversationId !== loadConversationId) return;

		clearStreamTimeout();
		activeRequestIdRef.current = undefined;
		requestGenerationRef.current += 1;

		const hydrated: ChatMessage[] = loadedConversation.messages.flatMap((msg) => [
			{ role: ROLE_USER, content: msg.userMessage, key: nextMessageKey(), timestamp: msg.createdAt },
			{
				role: ROLE_ASSISTANT,
				content: msg.bizyResponse,
				id: msg.id,
				feedbackType: msg.feedbackType ?? undefined,
				key: nextMessageKey(),
			},
		]);
		setMessages(hydrated);
		setConversationId(loadConversationId);
		setBizyEmotion(EMOTION_CURIOUS);
		setIsLoading(false);
		isLoadingRef.current = false;
		setIsTimedOut(false);
		setIsOtherTabLoading(false);
		setActiveToolCalls([]);
		streamingContentRef.current = '';
		setLoadConversationId(undefined);

		skipNextPersistRef.current = true;
		saveBizySession({ messages: hydrated, conversationId: loadConversationId, bizyEmotion: EMOTION_CURIOUS });
	}, [loadConversationId, loadedConversation, clearStreamTimeout]);

	// Clear loading state if the conversation fetch failed (e.g. network error)
	useEffect(() => {
		if (loadConversationId && isLoadError) setLoadConversationId(undefined);
	}, [loadConversationId, isLoadError]);

	// --- BizyChannel event handlers (own tab) ---
	const handleToken = useCallback(
		(content: string) => {
			if (!isLoadingRef.current) return;

			resetStreamTimeout();
			// Token events carry the full accumulated response (not a delta). Replace when
			// the incoming content is longer; ignore shorter out-of-order arrivals.
			if (content.length >= streamingContentRef.current.length) {
				streamingContentRef.current = content;
			}
			const updatedContent = streamingContentRef.current;

			setMessages((prev) => {
				const lastMessage = prev[prev.length - 1];
				if (lastMessage?.role === ROLE_ASSISTANT && !lastMessage.id) {
					return [...prev.slice(0, -1), { ...lastMessage, content: updatedContent }];
				}
				return [...prev, { role: ROLE_ASSISTANT, content: updatedContent, key: nextMessageKey() }];
			});
		},
		[resetStreamTimeout]
	);

	const handleComplete = useCallback(
		(data: BizyCompleteEvent['data']) => {
			// Connector response: replace the last assistant bubble with the full accumulated content
			if (data.connector_response && data.content) {
				setMessages((prev) => {
					const lastMessage = prev[prev.length - 1];
					if (lastMessage?.role === ROLE_ASSISTANT && lastMessage.id === data.message_id) {
						return [...prev.slice(0, -1), { ...lastMessage, content: data.content }];
					}
					return [...prev, { role: ROLE_ASSISTANT, content: data.content, id: data.message_id || undefined, key: nextMessageKey() }];
				});
				setIsTimedOut(false);
				if (isLoadingRef.current) {
					clearStreamTimeout();
					setIsLoading(false);
					isLoadingRef.current = false;
					setIsOtherTabLoading(false);
					setActiveToolCalls([]);
					streamingContentRef.current = '';
					messageSentAtRef.current = null;
				}
				return;
			}

			if (!isLoadingRef.current) return;

			clearStreamTimeout();

			setConversationId(data.conversation_id);

			// connector_processing: conductor is done but a connector is still working.
			// Strip the conductor's streamed text and keep thinking. Skip analytics —
			// the user hasn't received a visible response yet.
			if (data.connector_processing) {
				setMessages((prev) => {
					const lastMessage = prev[prev.length - 1];
					if (lastMessage?.role === ROLE_ASSISTANT && !lastMessage.id) {
						return prev.slice(0, -1);
					}
					return prev;
				});
				setActiveToolCalls([]);
				streamingContentRef.current = '';
				resetStreamTimeout();
				return;
			}

			const ttftMs = messageSentAtRef.current && data.first_token_ms ? data.first_token_ms : undefined;

			track(AnalyticsEvent.Bizy.ResponseReceived, {
				responseTimeMs: data.response_time_ms,
				emotion: data.emotion || EMOTION_CURIOUS,
				success: true,
				toolsUsed: data.tools_used || [],
				ttftMs,
				path: window.location.pathname,
			});

			const toolsUsed = data.tools_used?.length ? data.tools_used : undefined;

			setMessages((prev) => {
				const lastMessage = prev[prev.length - 1];
				if (lastMessage?.role === ROLE_ASSISTANT) {
					return [...prev.slice(0, -1), { ...lastMessage, content: data.content, id: data.message_id || undefined, toolsUsed }];
				}
				return [...prev, { role: ROLE_ASSISTANT, content: data.content, id: data.message_id || undefined, key: nextMessageKey(), toolsUsed }];
			});

			setBizyEmotion((data.emotion as BizyState) || EMOTION_CURIOUS);
			setIsLoading(false);
			isLoadingRef.current = false;
			setIsOtherTabLoading(false);
			setActiveToolCalls([]);
			streamingContentRef.current = '';
			messageSentAtRef.current = null;
		},
		[clearStreamTimeout, resetStreamTimeout]
	);

	const handleError = useCallback(
		(message: string) => {
			if (!isLoadingRef.current) return;

			clearStreamTimeout();

			track(AnalyticsEvent.Bizy.ResponseReceived, {
				success: false,
				errorType: 'streaming_error',
				path: window.location.pathname,
			});

			setMessages((prev) => {
				const lastMessage = prev[prev.length - 1];
				if (lastMessage?.role === ROLE_ASSISTANT && !lastMessage.id) {
					return [...prev.slice(0, -1), { ...lastMessage, content: message }];
				}
				return [...prev, { role: ROLE_ASSISTANT, content: message, key: nextMessageKey() }];
			});

			setBizyEmotion(EMOTION_DISAPPOINTED);
			setIsLoading(false);
			isLoadingRef.current = false;
			setIsOtherTabLoading(false);
			setActiveToolCalls([]);
			streamingContentRef.current = '';
			messageSentAtRef.current = null;
		},
		[clearStreamTimeout]
	);

	const handleCancelled = useCallback(
		(_content: string) => {
			if (!isLoadingRef.current) return;

			clearStreamTimeout();

			setMessages((prev) => {
				const last = prev[prev.length - 1];
				if (!last) return prev;
				if (last.role === ROLE_ASSISTANT && !last.id) {
					const secondLast = prev[prev.length - 2];
					if (secondLast?.role === ROLE_USER) return prev.slice(0, -2);
					return prev.slice(0, -1);
				}
				if (last.role === ROLE_USER) return prev.slice(0, -1);
				return prev;
			});

			setIsLoading(false);
			isLoadingRef.current = false;
			setIsOtherTabLoading(false);
			setActiveToolCalls([]);
			streamingContentRef.current = '';
			messageSentAtRef.current = null;
		},
		[clearStreamTimeout]
	);

	const handleToolCall = useCallback(
		(data: { tool_name: string }) => {
			if (!isLoadingRef.current) return;
			resetStreamTimeout();
			setActiveToolCalls((prev) => [...prev, data.tool_name]);
		},
		[resetStreamTimeout]
	);

	const handlePageAction = useCallback(
		async (data: any) => {
			if (!isLoadingRef.current) return;
			resetStreamTimeout();

			try {
				if (data.action === 'navigate_to_page') {
					if (data.page_path) {
						const path = String(data.page_path);
						if (path.startsWith('/') && !path.startsWith('//')) {
							saveBizySession({
								messages: messagesRef.current,
								conversationId: conversationIdRef.current,
								bizyEmotion: bizyEmotionRef.current,
							});
							navigate(path);
							requestAnimationFrame(() => {
								const el = document.querySelector('[data-bizy-chat-scroll]');
								if (el) el.scrollTop = el.scrollHeight;
							});
						}
					}
					return;
				}

				if (data.action === 'invalidate_celebration_queries') {
					invalidateCelebrationsAdminCaches(queryClient);
					return;
				}

				if (data.action === 'invalidate_recognition_queries') {
					invalidateBonusesInfinite();
					return;
				}

				if (data.action === 'invalidate_meetings_queries') {
					queryClient.invalidateQueries({ queryKey: [QueryKey.MeetingsV2] });
					return;
				}

				const pageId = data.report_type;
				const schema = getSchema(pageId);
				await executePageAction(data, schema, navigate);
			} catch (error) {
				console.error('[Bizy] Failed to execute action:', error);
			}
		},
		[getSchema, invalidateBonusesInfinite, navigate, queryClient, resetStreamTimeout]
	);

	const handleDisconnected = useCallback(() => {
		if (isLoadingRef.current) {
			handleTimeout('connection_lost');
		}
	}, [handleTimeout]);

	// --- Cross-tab event handlers ---
	const handleOtherTabComplete = useCallback(
		(data: BizyCompleteEvent['data']) => {
			if (data.connector_processing) return;
			setIsOtherTabLoading(false);
			if (data.conversation_id && conversationIdRef.current === data.conversation_id && !isLoadingRef.current) {
				refetchConversation(data.conversation_id);
			}
		},
		[refetchConversation]
	);

	const handleOtherTabError = useCallback(() => {
		setIsOtherTabLoading(false);
	}, []);

	const handleOtherTabLoading = useCallback(() => {
		setIsOtherTabLoading(true);
	}, []);

	// Subscribe to BizyChannel
	useBizyChannel(currentUser?.id, {
		onToken: handleToken,
		onComplete: handleComplete,
		onError: handleError,
		onToolCall: handleToolCall,
		onPageAction: handlePageAction,
		onCancelled: handleCancelled,
		onPreliminaryToken: resetStreamTimeout,
		onDisconnected: handleDisconnected,
		onOtherTabComplete: handleOtherTabComplete,
		onOtherTabError: handleOtherTabError,
		onOtherTabLoading: handleOtherTabLoading,
		activeRequestIdRef,
	});

	useEffect(() => clearStreamTimeout, [clearStreamTimeout]);

	// Persist state to sessionStorage whenever it changes (skip once after resume so we don't overwrite with stale state).
	useEffect(() => {
		if (skipNextPersistRef.current) {
			skipNextPersistRef.current = false;
			return;
		}
		saveBizySession({ messages, conversationId, bizyEmotion });
	}, [messages, conversationId, bizyEmotion]);

	// Recover truncated messages on mount.
	// If sessionStorage has an assistant message without an id, the complete
	// event was never received (killed worker, page refresh mid-stream, etc.).
	// Refetch from backend to recover.
	useEffect(() => {
		if (!sessionData?.conversationId || !sessionData?.messages?.length) return;
		const lastMsg = sessionData.messages[sessionData.messages.length - 1];
		if (lastMsg.role !== ROLE_ASSISTANT || lastMsg.id) return;

		refetchConversation(sessionData.conversationId).then(() => {
			// If refetch fails or returns nothing useful, drop the partial message
			setMessages((prev) => {
				const last = prev[prev.length - 1];
				if (last?.role === ROLE_ASSISTANT && !last.id) {
					return prev.slice(0, -1);
				}
				return prev;
			});
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount with initial session data
	}, []);

	const sendMessage = useCallback(
		(content: string) => {
			const currentMessageCount = messages.length;

			track(AnalyticsEvent.Bizy.ChatMessageSent, {
				messageLength: content.length,
				conversationLength: currentMessageCount,
				isFirstMessage: currentMessageCount === 0,
				path: window.location.pathname,
			});

			setMessages((prev) => [...prev, { role: ROLE_USER, content, key: nextMessageKey(), timestamp: new Date() }]);
			setIsLoading(true);
			setIsTimedOut(false);
			setIsOtherTabLoading(false);
			activeRequestIdRef.current = nextPendingRequestId();
			isLoadingRef.current = true;
			streamingContentRef.current = '';
			messageSentAtRef.current = Date.now();
			lastUserMessageRef.current = content;
			requestGenerationRef.current += 1;
			const generation = requestGenerationRef.current;

			resetStreamTimeout();

			const context: Record<string, string> = {
				page_path: window.location.pathname,
				...reportContext,
				...(extraContext || {}),
			};

			mutate(
				{ message: content, conversation_id: conversationId, context },
				{
					onSuccess: (data) => {
						if (requestGenerationRef.current !== generation) return;
						if (data.conversation_id) {
							setConversationId(data.conversation_id);
						}
						if (data.request_id) {
							activeRequestIdRef.current = data.request_id;
						}
					},
					onError: (error: any) => {
						if (requestGenerationRef.current !== generation) return;
						clearStreamTimeout();

						const isBusy = error?.response?.status === 409;

						track(AnalyticsEvent.Bizy.ResponseReceived, {
							success: false,
							errorType: isBusy ? 'conversation_busy' : error instanceof Error ? error.name : 'unknown',
							path: window.location.pathname,
						});

						if (isBusy) {
							// Don't show an error — set other-tab loading state instead.
							// Remove the user message we optimistically added (it wasn't accepted).
							setMessages((prev) => {
								const last = prev[prev.length - 1];
								if (last?.role === ROLE_USER) return prev.slice(0, -1);
								return prev;
							});
							setIsOtherTabLoading(true);
							setIsLoading(false);
							isLoadingRef.current = false;
						} else {
							setMessages((prev) => [...prev, { role: ROLE_ASSISTANT, content: t('genericError'), key: nextMessageKey() }]);
							setBizyEmotion(EMOTION_DISAPPOINTED);
							setIsLoading(false);
							isLoadingRef.current = false;
						}

						setActiveToolCalls([]);
						streamingContentRef.current = '';
						messageSentAtRef.current = null;
					},
				}
			);
		},
		[mutate, conversationId, messages.length, reportContext, extraContext, resetStreamTimeout, clearStreamTimeout, t]
	);

	const retryLastMessage = useCallback(() => {
		if (!lastUserMessageRef.current) return;
		setMessages((prev) => {
			const last = prev[prev.length - 1];
			if (last?.role === ROLE_ASSISTANT && !last.id) return prev.slice(0, -1);
			return prev;
		});
		setIsTimedOut(false);
		setIsOtherTabLoading(false);
		activeRequestIdRef.current = nextPendingRequestId();
		setIsLoading(true);
		isLoadingRef.current = true;
		streamingContentRef.current = '';
		messageSentAtRef.current = Date.now();
		requestGenerationRef.current += 1;
		const generation = requestGenerationRef.current;

		resetStreamTimeout();

		const context: Record<string, string> = {
			page_path: window.location.pathname,
			...reportContext,
			...(extraContext || {}),
		};

		mutate(
			{ message: lastUserMessageRef.current, conversation_id: conversationId, context },
			{
				onSuccess: (data) => {
					if (requestGenerationRef.current !== generation) return;
					if (data.conversation_id) setConversationId(data.conversation_id);
					if (data.request_id) activeRequestIdRef.current = data.request_id;
				},
				onError: () => {
					if (requestGenerationRef.current !== generation) return;
					clearStreamTimeout();
					setMessages((prev) => [...prev, { role: ROLE_ASSISTANT, content: t('genericError'), key: nextMessageKey() }]);
					setBizyEmotion(EMOTION_DISAPPOINTED);
					setIsLoading(false);
					isLoadingRef.current = false;
					setIsTimedOut(false);
					setActiveToolCalls([]);
					streamingContentRef.current = '';
					messageSentAtRef.current = null;
				},
			}
		);
	}, [mutate, conversationId, reportContext, extraContext, resetStreamTimeout, clearStreamTimeout, t]);

	const cancelGeneration = useCallback((): string | undefined => {
		if (!isLoadingRef.current) return undefined;

		requestGenerationRef.current += 1;
		const currentMessages = messagesRef.current;
		let restoredText: string | undefined;

		setMessages((prev) => {
			const last = prev[prev.length - 1];
			if (!last) return prev;

			if (last.role === ROLE_ASSISTANT && !last.id) {
				const secondLast = prev[prev.length - 2];
				if (secondLast?.role === ROLE_USER) {
					restoredText = secondLast.content;
					return prev.slice(0, -2);
				}
				return prev.slice(0, -1);
			}

			if (last.role === ROLE_USER) {
				restoredText = last.content;
				return prev.slice(0, -1);
			}

			return prev;
		});

		if (!restoredText) {
			const lastUser = [...currentMessages].reverse().find((m) => m.role === ROLE_USER);
			if (lastUser) restoredText = lastUser.content;
		}

		clearStreamTimeout();
		setIsLoading(false);
		isLoadingRef.current = false;
		setActiveToolCalls([]);
		streamingContentRef.current = '';
		messageSentAtRef.current = null;

		if (activeRequestIdRef.current) {
			cancelMutate({ request_id: activeRequestIdRef.current, conversation_id: conversationIdRef.current });
		}

		track(AnalyticsEvent.Bizy.GenerationCancelled, {
			path: window.location.pathname,
		});

		return restoredText;
	}, [clearStreamTimeout, cancelMutate]);

	const resetChat = useCallback(() => {
		clearStreamTimeout();
		activeRequestIdRef.current = undefined;
		requestGenerationRef.current += 1;

		setLoadConversationId(undefined);
		setMessages([]);
		setConversationId(undefined);
		setBizyEmotion(EMOTION_CURIOUS);
		setIsLoading(false);
		setIsTimedOut(false);
		setIsOtherTabLoading(false);
		isLoadingRef.current = false;
		setActiveToolCalls([]);
		streamingContentRef.current = '';
		clearBizySession();
	}, [clearStreamTimeout]);

	const updateMessageFeedback = useCallback((messageId: string, feedbackType: FeedbackType) => {
		setMessages((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, feedbackType } : msg)));
	}, []);

	const loadConversation = useCallback((id: string) => {
		setLoadConversationId(id);
	}, []);

	// Keep true until effect clears loadConversationId after applying data; avoids one-frame flash of previous messages
	const isLoadingConversation = Boolean(loadConversationId);

	return {
		messages,
		conversationId,
		isLoading,
		isTimedOut,
		isOtherTabLoading,
		isLoadingConversation,
		bizyEmotion,
		activeToolCalls,
		sendMessage,
		cancelGeneration,
		resetChat,
		retryLastMessage,
		updateMessageFeedback,
		loadConversation,
	};
}
